import {
  syncCallBetFlags,
  resolveDesiredCallBetPrice,
} from "../../utility/callBetSlip.js";

/** Half-point delta between book line and selected line. */
export function computeBuyPointsDelta(originalPoint, finalPoint) {
  const base = Number(originalPoint);
  const final = Number(finalPoint);
  if (!Number.isFinite(base) || !Number.isFinite(final)) return 0;
  return Math.round(Math.abs(final - base) * 100) / 100;
}

export function parseAmericanOddsInput(value) {
  if (value === "" || value == null || value === undefined) return NaN;
  const raw = String(value).trim();
  if (!raw || raw === "-") return NaN;
  const n = Number(raw);
  return Number.isFinite(n) ? n : NaN;
}

export function parseSpreadLineInput(value) {
  if (value === "" || value == null || value === undefined) return NaN;
  const raw = String(value).trim();
  if (!raw || raw === "-" || raw === "+" || raw.endsWith(".")) return NaN;
  const n = Number(raw);
  return Number.isFinite(n) ? n : NaN;
}

/** Allow partial line input while typing (e.g. "46.", "+2.5"). */
export function isValidPartialLineInput(value) {
  if (value === "" || value == null) return true;
  const raw = String(value).trim();
  return /^[+-]?(\d+\.?\d*|\.\d+)$/.test(raw);
}

/** Allow partial M-Line input while typing (e.g. "-", "+", "-6"). */
export function isValidPartialOddsInput(value) {
  if (value === "" || value == null) return true;
  const raw = String(value).trim();
  if (raw === "+" || raw === "-") return true;
  return /^[+-]?\d+$/.test(raw);
}

export function isCompleteLineInput(value) {
  const raw = String(value ?? "").trim();
  if (!raw || raw === "-" || raw === "+" || raw.endsWith(".")) return false;
  return Number.isFinite(parseSpreadLineInput(raw));
}

export function isCompleteOddsInput(value) {
  const raw = String(value ?? "").trim();
  if (!raw || raw === "+" || raw === "-") return false;
  return Number.isFinite(parseAmericanOddsInput(raw));
}

export function sanitizeDesiredLineInput(value) {
  if (value === "" || value == null) return "";
  let raw = String(value).replace(/[^\d.+-]/g, "");
  const sign = raw.startsWith("+") || raw.startsWith("-") ? raw[0] : "";
  raw = raw.replace(/^[+-]/, "");
  const parts = raw.split(".");
  const normalized =
    parts.length > 1 ? `${parts[0]}.${parts.slice(1).join("")}` : parts[0];
  return `${sign}${normalized}`;
}

export function sanitizeDesiredOddsInput(value) {
  if (value === "" || value == null) return "";
  let raw = String(value).replace(/[^\d+-]/g, "");
  const sign = raw.startsWith("+") || raw.startsWith("-") ? raw[0] : "";
  raw = raw.replace(/^[+-]/, "").replace(/[+-]/g, "");
  return `${sign}${raw}`;
}

export function validateCallBetLeg(item) {
  const label = item?.teamName || "this selection";
  const market =
    item?.type === "h2h"
      ? "M-Line"
      : item?.type === "totals"
        ? "Total"
        : "Spread";

  if (item?.type === "h2h") {
    const desired = parseAmericanOddsInput(item.desiredPrice);
    const book = parseAmericanOddsInput(item.originalPrice ?? item.price);
    if (!Number.isFinite(desired)) {
      return { ok: false, message: `Enter a desired M-Line price for ${label}` };
    }
    if (!Number.isFinite(book)) {
      return { ok: false, message: `Book price unavailable for ${label}` };
    }
    if (desired === book) {
      return {
        ok: false,
        message: `Desired price must differ from the book price (${book > 0 ? "+" : ""}${book}) for ${label}`,
      };
    }
    return { ok: true, desiredPrice: desired };
  }

  const desired = parseSpreadLineInput(item.desiredPoint);
  const book = parseSpreadLineInput(item.originalPoint ?? item.point);
  if (!Number.isFinite(desired)) {
    return { ok: false, message: `Enter a desired ${market} line for ${label}` };
  }
  if (!Number.isFinite(book)) {
    return { ok: false, message: `Book line unavailable for ${label}` };
  }
  if (desired === book) {
    return {
      ok: false,
      message: `Desired line must differ from the book line (${book}) for ${label}`,
    };
  }
  return { ok: true, desiredPoint: desired };
}

export function applyCallBetValidationToLeg(item) {
  const isH2h = item?.type === "h2h";
  const raw = isH2h ? item?.desiredPrice : item?.desiredPoint;
  const rawStr = String(raw ?? "");

  const partialOk = isH2h
    ? isValidPartialOddsInput(rawStr)
    : isValidPartialLineInput(rawStr);

  if (!partialOk) {
    return { ...item, isCallBetType: "false" };
  }

  const complete = isH2h
    ? isCompleteOddsInput(rawStr)
    : isCompleteLineInput(rawStr);

  if (!complete) {
    return { ...item, isCallBetType: "false" };
  }

  const result = validateCallBetLeg(item);
  return {
    ...item,
    isCallBetType: result.ok ? "true" : "false",
  };
}

const isCallBetItem = (item) => {
  if (item?.isCallBetType === true || item?.isCallBetType === "true") {
    return true;
  }
  return validateCallBetLeg(item).ok;
};

/** Ensure buyPoints / line fields are sent on every bet placed. */
export function normalizeBetSlipItemForSubmit(item) {
  if (!item || typeof item !== "object") return item;

  const callSynced = syncCallBetFlags(item);
  const isCallBet =
    callSynced.isCallBetType === true || callSynced.isCallBetType === "true";

  if (isCallBet) {
    return {
      ...callSynced,
      buyPoints: 0,
      bookPrice: Number(
        callSynced.bookPrice ?? callSynced.originalPrice ?? callSynced.price
      ),
      bookPoint: Number(callSynced.bookPoint ?? callSynced.originalPoint ?? 0),
      desiredPrice: resolveDesiredCallBetPrice(callSynced),
    };
  }

  const originalPoint = item.originalPoint ?? item.point;
  const originalPrice = item.originalPrice ?? item.price;
  const callBet = isCallBetItem(item);

  if (callBet) {
    const isH2h = item.type === "h2h";
    const desiredPoint = isH2h
      ? undefined
      : parseSpreadLineInput(item.desiredPoint ?? item.customizedPoint);
    const desiredPrice = isH2h
      ? parseAmericanOddsInput(item.desiredPrice)
      : undefined;

    return {
      ...item,
      originalPoint,
      originalPrice,
      buyPoints: 0,
      customizedPoint: isH2h ? item.point : desiredPoint,
      desiredPoint: isH2h ? undefined : desiredPoint,
      desiredPrice: isH2h ? desiredPrice : undefined,
      isCallBetType: "true",
    };
  }

  const hasCustom =
    item.customizedPoint !== undefined &&
    item.customizedPoint !== "" &&
    item.customizedPoint !== "-";
  const customizedPoint = hasCustom ? item.customizedPoint : item.point;

  const explicit = Number(item.buyPoints);
  let buyPoints =
    Number.isFinite(explicit) && explicit >= 0 ? explicit : 0;

  if (buyPoints === 0) {
    buyPoints = computeBuyPointsDelta(originalPoint, customizedPoint);
  }

  return {
    ...item,
    originalPoint,
    originalPrice,
    customizedPoint,
    buyPoints,
    isCallBetType: "false",
  };
}

/** Flat slip legs or nested submit payload → parlay leg array. */
export function extractParlayLegs(data) {
  if (!Array.isArray(data) || data.length === 0) return [];

  if (Array.isArray(data[0])) {
    return data[0].filter((leg) => leg && typeof leg === "object");
  }

  return data.filter(
    (leg) => leg && typeof leg === "object" && (leg.teamName || leg.id)
  );
}

export function normalizeBetsForSubmit(bets, type) {
  if (!Array.isArray(bets)) return bets;

  if (type === "parlay") {
    const legs = bets.map(normalizeBetSlipItemForSubmit);
    return [legs];
  }

  return bets.map(normalizeBetSlipItemForSubmit);
}

export function slipHasCallBet(legs) {
  if (!Array.isArray(legs)) return false;
  return legs.some(isCallBetItem);
}

export function slipIsCallBetOnly(legs) {
  if (!Array.isArray(legs) || legs.length === 0) return false;
  return legs.every(isCallBetItem);
}
